import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import complaintApi from '../services/complaintApi';

// The Complaints page, the menu counter and the «فتح شكوى» form (tasks/complaints-to-fixes.md). Everything is
// a SERVER verdict: every action invalidates the reads rather than patching a row locally — a client-side
// guess at the new state is how a surface starts disagreeing with the server.

export const COMPLAINT_QUEUE_KEY = ['complaints-queue'];
export const COMPLAINT_COUNTS_KEY = ['complaints-counts'];
const DETAIL_KEY = 'complaint-detail';
const PREFILL_KEY = 'complaint-prefill';
const GUEST_VOICE_KEY = 'guest-voice';   // useGuestReviews.useGuestVoice

// Stable empty shape, so the page never crashes before the first fetch or on an error.
const EMPTY_QUEUE = {
  counts: { needs_action: 0, in_progress: 0, closed: 0, badge: 0 },
  items: [],
  defaults: { scheduledDate: null },
  options: { supplies: [] }
};

export const useComplaintQueue = () => {
  const queryClient = useQueryClient();
  const result = useQuery({
    queryKey: COMPLAINT_QUEUE_KEY,
    // The page's read carries the same counts as the menu counter's (one server definition). Handing them to
    // the counter's cache keeps the list, the tab title and the menu on ONE number — the counter otherwise
    // waits for its own two-minute poll after a task changed elsewhere (dev walkthrough, 14 Oct).
    queryFn: async () => {
      const data = (await complaintApi.getQueue()).data;
      queryClient.setQueryData(COMPLAINT_COUNTS_KEY, data.counts);
      return data;
    },
    // Deadlines are hours and days, not seconds — a minute is fresh enough for "is it late yet".
    refetchInterval: 60 * 1000,
    staleTime: 15 * 1000
  });
  return { ...result, queue: result.data ?? EMPTY_QUEUE };
};

/**
 * The menu counter. `enabled` only for core staff — the endpoint refuses everyone else. `poll: false` for a
 * second reader of the same count (the page's tab title): it shares the menu's cache without running a
 * second timer — the menu keeps it fresh, and every action invalidates it.
 */
export const useComplaintCounts = (enabled, { poll = true } = {}) => useQuery({
  queryKey: COMPLAINT_COUNTS_KEY,
  queryFn: async () => (await complaintApi.getCounts()).data.counts,
  enabled: Boolean(enabled),
  refetchInterval: poll ? 2 * 60 * 1000 : false,
  staleTime: 60 * 1000
});

/** One complaint's history — read when its card is opened, never before. */
export const useComplaintHistory = (id, enabled) => useQuery({
  queryKey: [DETAIL_KEY, String(id)],
  queryFn: async () => (await complaintApi.getOne(id)).data.history,
  enabled: Boolean(enabled && id)
});

/** Where the «فتح شكوى» form starts. Read afresh each time the form opens — no stale units or complaints. */
export const useComplaintPrefill = (source, enabled) => useQuery({
  queryKey: [PREFILL_KEY, source?.kind, String(source?.id), source?.topic || null],
  queryFn: async () => (await complaintApi.prefill(source)).data,
  enabled: Boolean(enabled && source?.kind && source?.id),
  staleTime: 0,
  retry: false
});

/** The accountable-person and assignee pickers. */
export const useComplaintPeople = (enabled) => useQuery({
  queryKey: ['complaint-people'],
  queryFn: complaintApi.getPeople,
  enabled: Boolean(enabled),
  staleTime: 5 * 60 * 1000
});

// onSettled, not onSuccess: a refusal usually means the page was stale (someone else acted), which is
// exactly when a refresh is most needed. The promise is RETURNED so the action stays pending until the fresh
// list is in — no button is live on a stale card between the click and the refresh.
const useAction = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: COMPLAINT_QUEUE_KEY }),
      queryClient.invalidateQueries({ queryKey: COMPLAINT_COUNTS_KEY }),
      queryClient.invalidateQueries({ queryKey: [DETAIL_KEY] }),
      queryClient.invalidateQueries({ queryKey: [PREFILL_KEY] }),
      // «صوت الضيف» rows show a topic's complaint — opening or closing one changes the row (guest-voice G7, G11).
      queryClient.invalidateQueries({ queryKey: [GUEST_VOICE_KEY] })
    ])
  });
};

export const useCreateComplaint = () => useAction(complaintApi.create);
export const useAddComplaintSource = () => useAction(complaintApi.addSource);
export const useEditComplaint = () => useAction(complaintApi.edit);
export const useCreateRepairTask = () => useAction(complaintApi.createTask);
export const useCloseComplaint = () => useAction(complaintApi.close);
export const useReopenComplaint = () => useAction(complaintApi.reopen);
